/**
 * SurveyGate — asks the server whether a survey is due, and shows it.
 *
 * TWO THINGS MAKE THIS DELIBERATELY NOT A useQuery:
 *
 * 1. `fetchActiveSurvey` is NOT a pure read. It CLAIMS the member's single
 *    showing by writing the survey_invites row. A React Query with retries,
 *    refetch-on-focus or refetch-on-mount would burn that claim repeatedly (the
 *    repeats are harmless — the invite already exists so they return null — but
 *    the FIRST one is irreversible, so it must be fired on purpose exactly once).
 * 2. Two placements are mounted at once (`login` and `community_enter`). Without
 *    coordination a member entering community right after signing in could be
 *    asked twice in one session. The module-level `claimedThisSession` latch is
 *    that coordination — first gate to find a survey wins, the other stands down.
 *
 * The latch is module-level, not state: it must survive remounts as the member
 * navigates, and it must be shared across both placements. It resets on reload,
 * which is correct — a reload is a new session.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useCurrentUser } from '@/api/hooks';
import {
  dismissSurveyInvite,
  fetchActiveSurvey,
  snoozeSurveyInvite,
  submitSurveyResponse,
  type ActiveSurvey,
  type SurveyAnswers,
  type SurveyPlacement,
} from '@/api/surveys';
import { SurveyModal } from './SurveyModal';

/** One survey per session, across every placement. See the header. */
let claimedThisSession = false;

export interface SurveyGateProps {
  placement: SurveyPlacement;
}

export function SurveyGate({ placement }: SurveyGateProps) {
  const { data: user } = useCurrentUser();
  const [survey, setSurvey] = useState<ActiveSurvey | null>(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const askedRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;
    if (askedRef.current || claimedThisSession) return;
    askedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const next = await fetchActiveSurvey(placement);
        if (cancelled || !next) return;
        // Re-check the latch: another placement may have claimed while we awaited.
        if (claimedThisSession) return;
        claimedThisSession = true;
        setSurvey(next);
        setOpen(true);
      } catch {
        // A survey is never important enough to surface an error to a member who
        // did not ask for one. Stay silent and try again next session.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, placement]);

  if (!survey) return null;

  const handleSubmit = async (answers: SurveyAnswers, anonymous: boolean) => {
    setSubmitting(true);
    setError(null);
    try {
      await submitSurveyResponse(survey.id, answers, anonymous);
      setOpen(false);
      setSurvey(null);
      toast('Thanks — that helps.', { description: 'Your feedback has been recorded.' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * ✕ / Esc / backdrop — "not this, ever". The member carries on to wherever
   * they were going; nothing else happens. The survey is not blocking, so
   * closing it must cost nothing and lead nowhere.
   *
   * Fire-and-forget: the rest floor is what stops a re-show, so failing to
   * stamp dismissed_at costs a results-view stat, never a repeat.
   */
  const handleDismiss = () => {
    setOpen(false);
    const id = survey.id;
    setSurvey(null);
    void dismissSurveyInvite(id).catch(() => {});
  };

  /**
   * "Ask me later" — "not now". Unlike dismissal this one is worth confirming,
   * because the member is expecting it back and a silent failure would look
   * like the button did nothing.
   */
  const handleSnooze = () => {
    setOpen(false);
    const id = survey.id;
    setSurvey(null);
    void snoozeSurveyInvite(id)
      .then(() => toast('No problem — we’ll ask again later.'))
      .catch(() => {});
  };

  return (
    <SurveyModal
      survey={survey}
      open={open}
      submitting={submitting}
      error={error}
      onSubmit={handleSubmit}
      onDismiss={handleDismiss}
      onSnooze={handleSnooze}
    />
  );
}

export default SurveyGate;
